<script lang="ts" setup>
import type { RouteStopDto } from "@/api/trips/dto/trip.dto";
import { OrderStopShortTypeMap } from "@/constants/order";
import type { TripRouteFreight } from "@/types/trip";
import { computed, onMounted, ref } from "vue";
import { useFreights } from "@/composables";
import { useValidation } from "@/composables/useValidation";
import { useI18n } from "vue-i18n";

const { getFreightName } = useFreights();
const { requiredValidator } = useValidation();
const { t } = useI18n();

const model = defineModel<TripRouteFreight[]>({ default: [] });

const props = defineProps<{
  routeStops: RouteStopDto[];
  pickUp: RouteStopDto;
  index: number;
  disabled: boolean;
}>();

const isEdit = ref<boolean>(false);
const deliveryFreights = ref<TripRouteFreight[]>([]);

const stopPosition = computed(() => {
  const relatedStopsByType = props.routeStops.filter((stop) => stop.type === props.pickUp.type);

  return relatedStopsByType.findIndex((stop) => stop.id === props.pickUp.id) + 1;
});

const stopInfo = computed(() => {
  return `#${props.index + 1}: ${OrderStopShortTypeMap[props.pickUp.type]} #${stopPosition.value || ""} (${props.pickUp.facility?.fullAddress})`;
});

const hasEmptyBolNumber = computed(() => {
  return deliveryFreights.value.some((item) => !item.bolNumber);
});

const pickUpFreightIds = computed(() => {
  return new Set(props.pickUp.freights);
});

function saveChanges() {
  isEdit.value = false;

  model.value = model.value.map((freight) => {
    const updated = deliveryFreights.value.find((item) => item.id === freight.id);

    if (updated) {
      return {
        ...updated,
      };
    }

    return freight;
  });
}

onMounted(() => {
  deliveryFreights.value = model.value
    .filter((freight) => pickUpFreightIds.value.has(freight.id))
    .map((freight) => {
      return {
        ...freight,
      };
    });
});
</script>

<template>
  <div class="pa-4 bg-grey-lighten-4 rounded-lg mt-3">
    <div class="d-flex justify-space-between">
      <div>
        <div class="font-size-16 text-grey-darken-3 mb-4">{{ stopInfo }}</div>

        <div
          v-for="freight in deliveryFreights"
          :key="freight.id"
          :class="{
            'mt-2': index !== 0,
          }"
          class="font-size-16 text-grey-darken-3 font-weight-500"
        >
          <div class="d-flex align-center col-gap-2">
            <span>{{ getFreightName(freight, null, "", false, false) }},</span>

            <span v-if="!isEdit">BOL/POD #{{ freight.bolNumber }}</span>

            <v-text-field
              v-else
              v-model="freight.bolNumber"
              :rules="[requiredValidator]"
              class="required"
              density="compact"
              flat
              hide-details
              label="BOL/POD number"
              variant="outlined"
            ></v-text-field>
          </div>
        </div>
      </div>

      <v-btn
        v-if="!isEdit"
        :disabled="disabled"
        color="primary"
        flat
        prepend-icon="mdi-pencil-outline"
        size="small"
        variant="outlined"
        @click="isEdit = true"
      >
        {{ t("general.edit-details") }}
      </v-btn>

      <v-btn
        v-else
        :disabled="hasEmptyBolNumber"
        color="primary"
        flat
        prepend-icon="mdi-check"
        size="small"
        @click="saveChanges"
      >
        {{ t("general.save-changes") }}
      </v-btn>
    </div>
  </div>
</template>
